import React from 'react';
import { Download, Link2, ShieldCheck } from 'lucide-react';
import { BUTTON_LABEL, BUTTON_HINT } from '@/components/FootagePullButton';

/**
 * "What's new" — Footage Pull, ADMIN/STAFF variant.
 *
 * Single screen, no steps: admin's own work does not change. What they need is the
 * shape of the change (editors get footage through the app, not the NAS or
 * MediaShuttle), what they now see on a project, and what to answer when an editor
 * says it did not work. The editor variant teaches the workflow.
 *
 * Product copy is imported (the button's strings from FootagePullButton.js); the
 * rest is announcement prose with no other home.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

export function FootagePullAdminAnnouncement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-footage-pull-admin">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Download className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Editors get footage through Footage Pull now</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          Editors download a project’s footage with a small app when the client sent it through Dropbox, Google Drive
          or WeTransfer. For those three they no longer go to the NAS or MediaShuttle. Nothing changes in what you do;
          this is what you will see, and what to answer.
        </p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What you see on a project</Eyebrow>
        <div className="rounded-lg border border-ug-border-strong bg-ug-surface p-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* The real button's classes and strings. Not clickable here. */}
            <span className="ug-cta tap-auto font-sans text-sm shadow-none pointer-events-none">
              <span className="ug-cta-inner"><Download className="w-4 h-4" /> {BUTTON_LABEL}</span>
            </span>
            <span className="text-[10px] text-slate-500">{BUTTON_HINT}</span>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">On the Files tab of every project, for you and staff as well as the editor.</p>
        </div>
        <div className="flex flex-col gap-1.5">
          {[[Link2, '“show links” beside the button lists the client’s raw links, to open or copy'],
            [ShieldCheck, 'The link verdict on the Files tab is what the app relies on: a dead, walled or malformed link is refused; ok or short is pulled'],
            [Download, 'The Editor Guide carries the app downloads and the first-run steps for Mac and Windows']].map(([Icon, text], i) => (
            <div key={i} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-zinc-100/75">{text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 py-5 flex flex-col gap-2.5 border-t border-ug-border-subtle">
        {[
          ['“It did not open”', 'The app has to be opened once by hand after installing, so the computer learns to hand projects to it. Then the button works. The first open shows a security warning because the app is new to that computer; the guide has the exact clicks.'],
          ['“It refused the link”', 'The link check found it dead, walled or malformed. Ask the client for a fresh share. A file request works for this, and a link in their reply joins the project on its own.'],
          ['WeTransfer is checked by size only', 'Dropbox and Google Drive files are verified against a checksum. WeTransfer provides none, so those files are checked by size, and the app says so when it finishes. Nothing to do unless the size looks wrong.'],
          ['A pull while the client is still uploading is flagged, not blocked', 'When a client has an upload marked as still in progress, the button, the app’s start line and its done line all say the set may be incomplete. It clears on its own once they flip the flag or the link check lists the full declared size. “Not started” never warns: most cloud clients never touch it.'],
          ['The NAS and MediaShuttle are still used for everything else', 'A client who sends a hard drive, or uses a service other than those three, is handled exactly as before: the footage goes through the NAS or MediaShuttle and the editor gets it there. Nothing already on them needs moving.'],
        ].map(([t, d], i) => (
          <div key={i} className="flex gap-3">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
            <p className="text-[12px] text-zinc-100/75 leading-relaxed">
              <span className="text-zinc-100 font-medium">{t}.</span> {d}
            </p>
          </div>
        ))}
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default FootagePullAdminAnnouncement;
